import { useRecruitmentApi } from 'infrastructure/api';
import { useCrudService } from 'framework/crud';
import { FormFieldsData } from 'framework/form';
import { cloneDeep } from 'lodash';
import {
  CompensationCurrency,
  DetailsCompensation,
  Vacancy,
  VacancyFormType,
  VacancyGroupPositionSlotData,
  VacancyGroupType,
} from './types';
import createVacancySchema from './validation/createVacancySchema';
import useHiringService, { HIRING_MODE } from './useHiringService';
import {
  DAYS_TO_CLOSE,
  LOCATION_ACCOMMODATIONS,
  NO_ACCESS_MESSAGE,
  RELOCATION,
  VACANCIES_GROUPS,
  VACANCY_CREATE_FLOW,
} from './constants';
import updateVacancySchema from './validation/updateVacancySchema';
import updateRequiresReviewVacancySchema from './validation/updateRequiresReviewVacancySchema';
import closePositionValidationSchema from './validation/closePositionValidationSchema';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import { useRecruitingFinanceService } from 'domain/shared/recruitingFinance';
import { useCallback } from 'react';
import { usePositionService, useEmployeeNamesService } from 'domain/shared/employee';
import { CURRENCIES } from 'framework/ui/field';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';

const useVacancyGroupService = () => {
  const { sendPostRequest, sendGetRequest, sendPutRequest, sendPatchRequest } = useRecruitmentApi();
  const { getEmployeeNamesByAliases, getEmployeeNameDataById } = useEmployeeNamesService();
  const { saveSensitiveRecruitingInfo, searchSensitiveRecruitingInfo } = useRecruitingFinanceService();
  const { searchForMigration } = useHiringService();
  const { getCurrentDateTimeInUtc } = useDateService();
  const currentDate = getCurrentDateTimeInUtc();
  const { findAll: findAllPositions } = usePositionService();
  const { isFeatureEnabled } = useFeatureToggle();
  const positionSlotsEnabled = isFeatureEnabled(Features.HIRING_REQUEST_POSITION_SLOTS);
  const calculatedTargetHiringDate = currentDate.add(DAYS_TO_CLOSE, 'days').format(DATE_FORMAT.INPUT_VALUE);

  const { searchAll: baseSearchAll, ...baseCrud } = useCrudService({
    singleResourceUrl: `${VACANCIES_GROUPS}/:id`,
    listResourceUrl: VACANCIES_GROUPS,
    useService: useRecruitmentApi,
  });

  const { search: baseSearchVacancy } = useCrudService({
    singleResourceUrl: '/vacancies/:id',
    listResourceUrl: '/vacancies',
    useService: useRecruitmentApi,
  });

  const mapDataForForm = useCallback(
    async (showData: FormFieldsData) => {
      return {
        recruiters: await getEmployeeNamesByAliases(showData.recruiters),
        sourcers: await getEmployeeNamesByAliases(showData.sourcers),
        isCandidatesHidden: showData.isCandidatesHidden,
        isAutoDecisionTransitionDisabled: showData.isAutoDecisionTransitionDisabled,
        priority: showData.priority,
        specificNote: showData.specificNote,
        details: {
          coordinators: await getEmployeeNamesByAliases(showData.details.coordinators),
        },
      };
    },
    [getEmployeeNamesByAliases],
  );

  const mapOfficeToLocation = (office: number) => {
    switch (office) {
      case 1:
        return { country: 'US', city: 'Austin' };
      case 2:
        return { country: 'BY', city: 'Minsk' };
      case 3:
        return { country: 'PL', city: 'Krakow' };
      case 4:
        return { country: 'GE', city: 'Tbilisi' };
      case 5:
      default:
        return null;
    }
  };

  const getSpecializationOptions = async () => {
    const positions = await findAllPositions();

    return positions.map((position: Dictionary<string>) => {
      return { alias: position.vacancyAlias, name: position.name };
    }, []);
  };

  const searchVacancy = (request: any) => {
    const searchParams = cloneDeep(request);
    const searchString = searchParams.filter?.searchString?.ct || '';

    if (searchString && searchParams.filter) {
      delete searchParams.filter?.searchString;
      searchParams.filter.specialization = {
        ct: searchString,
      };
    }

    return baseSearchVacancy(searchParams);
  };

  const mapLegacyVacancy = async (vacancy: any) => {
    const hiringRequests = await searchForMigration(vacancy.hiringRequestIds, vacancy.alias);

    const group = {
      alias: vacancy.alias,
      specialization: vacancy.name,
      recruiter: null,
      sourcer: vacancy.sourcerAlias,
      createdAt: vacancy.createdAt,
      createdBy: vacancy.createdById,
      updatedAt: vacancy.updatedAt,
      updatedBy: vacancy.updatedById,
    };

    const vacancies = hiringRequests.map((request: any) => {
      const optionalRequirementsDescription = vacancy.description.optionalRequirements
        ? `<ul>${vacancy.description.optionalRequirements
            .map((responsibility: any) => `<li>${responsibility.name}: ${responsibility.description}</li>`)
            .join('')}</ul>`
        : '';

      return {
        status: {
          OPEN: 'OPEN',
          CLOSED: 'CLOSED',
          FAILED: 'EXPIRED',
          CANCELED: 'CANCELLED',
          MIGRATED: 'CLOSED',
        }[request.status as string],
        specialization: request.specialisation,
        competencyLevelMin: vacancy.competencyLevel === 'N/A' ? null : vacancy.competencyLevel,
        competencyLevelMax: vacancy.competencyLevel === 'N/A' ? null : vacancy.competencyLevel,
        isAsap: request.isAsap,
        targetHiringDate: request.isAsap === true ? calculatedTargetHiringDate : request.targetDate,
        expirationDate: request.expirationDate,
        hiringMode: request.hiringMode,
        workingConditions: vacancy.vacancyDetails.map((details: any) => ({
          registration: {
            1: 'US',
            2: 'BY',
            3: 'PL',
            5: 'PL',
          }[details.office as number],
          accommodation: {
            1: 'ANYWHERE',
            2: 'BY',
            3: 'EU',
            5: 'ANYWHERE',
          }[details.office as number],
          employmentTypes: details.assignmentType,
          contractTypes: details.contractType.map(
            (type: any) =>
              ({
                EC: 'EMPLOYMENT_CONTRACT',
                CFS: 'CONTRACT_FOR_SERVICES',
                B2B: 'B2B_CONTRACT',
                INTERNSHIP: 'INTERNSHIP',
              })[type as string],
          ),
          isRelocationAvailable: {
            REQUIRED: true,
            OPTIONAL: true,
            NOT_APPLICABLE: false,
          }[details.relocationOption as string],
        })),
        projects: [],
        responsibilities: `<ul>${vacancy.description.responsibilities
          .map((responsibility: any) => `<li>${responsibility.name}: ${responsibility.description}</li>`)
          .join('')}</ul>`,
        mandatoryRequirements: {
          description: `<ul>${vacancy.description.requirements
            .map((responsibility: any) => `<li>${responsibility.name}: ${responsibility.description}</li>`)
            .join('')}</ul>`,
          skills: vacancy.description.mandatorySkills,
          requiredExperienceInYears: vacancy.minExperience,
          englishLevel: 'B1',
        },
        optionalRequirements: {
          description: optionalRequirementsDescription,
          skills: vacancy.description.optionalSkills,
        },
        notes: {
          description: vacancy.description.projectText,
          attachmentIds: [],
        },
        positions: request.requestPositions.map((position: any) => ({
          type: {
            REQUIRED: 'REQUIRED',
            EXTRA: 'ADDITIONAL',
          }[position.necessityStatus as string],
          status: {
            OPEN: 'OPEN',
            CLOSED: 'CLOSED',
            CANCELED: 'CANCELLED',
          }[position.positionStatus as string],
          forecastDate: position.forecastDates?.[0]?.forecastDate ?? null,
          closeDate: position.closeDate ?? null,
          startWorkingDate: null,
        })),
        createdAt: request.createdAt,
        createdBy: request.createdBy,
      };
    });

    const vacancyDetails = vacancy.vacancyDetails.map((details: any) => ({
      location: mapOfficeToLocation(details.office),
      assignmentType: details.assignmentType,
      workingSchedule: details.workingSchedule,
      workplaceLocation: details.workplaceLocation,
      contractType: details.contractType.map(
        (type: any) =>
          ({
            EC: 'EMPLOYMENT_CONTRACT',
            CFS: 'CONTRACT_FOR_SERVICES',
            B2B: 'B2B_CONTRACT',
            INTERNSHIP: 'INTERNSHIP',
          })[type as string],
      ),
      relocationOption: details.relocationOption,
    }));

    const publications = vacancy.publications.map((publication: any) => ({
      description: publication.description,
      name: publication.name,
      type: publication.type,
      status: publication.status,
      englishLevels: publication.englishLevels,
      countryOfResidences: publication.countryOfResidences,
      formFields: publication.formFields,
      createdAt: publication.createdAt,
      createdBy: publication.createdBy,
      updatedAt: publication.updatedAt,
      updatedBy: publication.updatedBy,
      publicationDetails: vacancyDetails,
    }));

    return { group, vacancies, publications };
  };

  const migrateVacancy = async (
    group: any,
    vacancies: any[],
    publications: any[],
    migrateHiringRequests: boolean,
    vacancyGroupToMerge: string | null,
  ) => {
    if (!migrateHiringRequests && !vacancyGroupToMerge) {
      throw new Error('Unexpected error occurred. Please, reload the page.');
    }

    const vacanciesToMigrate = migrateHiringRequests ? vacancies : [];

    const result = await sendPostRequest('/vacancies/migrate', {
      group,
      vacancies: vacanciesToMigrate,
      publications,
    });
    const resultObject = await result.json();

    if (!migrateHiringRequests) {
      const vacanciesToMove = await searchVacancy({
        filter: {
          'vacancyGroup.id': {
            eq: vacancyGroupToMerge,
          },
        },
      });

      await Promise.all(vacanciesToMove.result.map((vacancy: any) => moveToGroup(vacancy.id, resultObject.id)));
    }
  };

  const visitGroup = async (vacancyGroupId: string) => {
    const result = await sendPostRequest(`/vacancies/groups/${vacancyGroupId}/visit`);
    return result.json();
  };

  const moveToGroup = async (vacancyId: string, vacancyGroupId: string | null) => {
    const result = await sendPatchRequest(`/vacancies/${vacancyId}/group`, { vacancyGroupId });
    return result.json();
  };

  const changePriority = async (vacancyGroupId: string, priority: string) => {
    const result = await sendPatchRequest(`/vacancies/groups/${vacancyGroupId}`, { priority });
    return result.json();
  };

  const mapWorkingConditionsForCreate = (workingConditions: VacancyFormType['workingConditions']) =>
    workingConditions.map(condition => ({
      hiringLocation: condition.hiringLocation,
      employmentTypes: condition.employmentTypes,
      contractTypes: condition.contractTypes,
      isRelocationAvailable: condition.isRelocationAvailable === RELOCATION.AVAILABLE,
    }));

  const mapCommonCreateValues = (values: VacancyFormType) => ({
    specialization: values.specialization,
    technology: values.technology,
    competencyLevel: values.competencyLevel,
    isAsap: values.isAsap,
    targetHiringDate: values.targetHiringDate,
    expirationPeriodInMonth: values.expirationPeriodInMonth,
    hiringMode: values.hiringMode,
    responsibilities: values.responsibilities,
    projects: values.projects,
    mandatoryRequirements: values.mandatoryRequirements,
    optionalRequirements: values.optionalRequirements,
    notes: values.notes,
    workingConditions: mapWorkingConditionsForCreate(values.workingConditions),
  });

  const mapDataForCreate = (values: VacancyFormType) => {
    const commonValues = mapCommonCreateValues(values);
    const isPositionSlotCreateFlow =
      positionSlotsEnabled && values.vacancyCreateFlow === VACANCY_CREATE_FLOW.POSITION_SLOTS;

    if (isPositionSlotCreateFlow) {
      return { ...commonValues, positionSlotIds: values.positionSlotIds };
    }

    return {
      ...commonValues,
      peopleToHireMin: values.peopleToHireMin,
      peopleToHireMax: values.peopleToHireMax,
    };
  };

  const mapVacancyGroup = async (
    values: VacancyGroupType,
    vacancies: Vacancy[],
    specializationsResponse: { alias: string; name: string }[],
  ) => {
    const {
      competencyLevelMax,
      hiringModes,
      competencyLevelMin,
      englishLevelMin,
      englishLevelMax,
      compensations,
      accommodations,
      ...details
    } = values.details;
    const cachedCompensationData = new Map<string, number>();

    const collectAllCompensationIds = (): string[] => {
      const compensationCurrencyIds = compensations
        .flatMap(c => c.compensationCurrencies ?? [])
        .flatMap(cur => [...(cur.compensationMinValueIds ?? []), ...(cur.compensationMaxValueIds ?? [])]);

      const vacancyCompensationIds = vacancies
        .flatMap(v => v.workingConditions ?? [])
        .flatMap(wc => [wc.compensation?.salaryFromId, wc.compensation?.salaryToId])
        .filter((id): id is string => Boolean(id));

      return Array.from(new Set([...compensationCurrencyIds, ...vacancyCompensationIds]));
    };

    const allIds = collectAllCompensationIds();
    if (allIds.length > 0) {
      try {
        const allCompensationData = await retrieveSensitiveRecruitingInfo(allIds);
        allCompensationData.forEach((value, key) => {
          const numericValue = Number(value);

          if (!isNaN(numericValue)) cachedCompensationData.set(key, numericValue);
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
    const formatCompensation = (min: number, max: number, currency: string) =>
      min !== Infinity && max !== -Infinity ? `${min}-${max} ${CURRENCIES[currency]}` : null;

    const findMinValue = (numArray: number[]) => (numArray.length ? Math.min(...numArray) : Infinity);
    const findMaxValue = (numArray: number[]) => (numArray.length ? Math.max(...numArray) : -Infinity);
    const retrieveCompensationRange = (minIds?: string[], maxIds?: string[], currency?: string) => {
      try {
        const minValues = (minIds ?? []).map(id => cachedCompensationData.get(id)).filter(v => v != null);
        const maxValues = (maxIds ?? []).map(id => cachedCompensationData.get(id)).filter(v => v != null);

        return formatCompensation(findMinValue(minValues), findMaxValue(maxValues), currency || 'USD');
      } catch {
        return NO_ACCESS_MESSAGE;
      }
    };

    const mapCompensationCurrencies = (compensationCurrencies: CompensationCurrency[]) => {
      return compensationCurrencies.map((compensationCurrency: CompensationCurrency) => ({
        value: retrieveCompensationRange(
          compensationCurrency.compensationMinValueIds,
          compensationCurrency.compensationMaxValueIds,
          compensationCurrency.currency,
        ),
      }));
    };

    const groupSalaryCompensations = compensations.map((compensation: DetailsCompensation) => {
      return {
        registration: compensation.registration,
        compensationCurrencies: mapCompensationCurrencies(compensation.compensationCurrencies),
      };
    });

    const mapVacancy = async (vacancy: Vacancy) => {
      const { competency, workingConditions, positions, ...data } = vacancy;

      const employeeDri = data.responsiblePerson ? await getEmployeeNameDataById(data.responsiblePerson) : null;
      const employeeStaffingCoordinator = await getEmployeeNameDataById(data.staffingCoordinator);

      return {
        coordinator: data.staffingCoordinator,
        staffingCoordinatorName: `${employeeStaffingCoordinator.nameEn.firstName} ${employeeStaffingCoordinator.nameEn.lastName}`,

        ...data,
        competency,
        responsiblePersonName: employeeDri ? `${employeeDri.nameEn.firstName} ${employeeDri.nameEn.lastName}` : '',
        workingConditions: await Promise.all(
          workingConditions.map(async item => ({
            ...item,
            isRelocationAvailable: item.isRelocationAvailable ? RELOCATION.AVAILABLE : RELOCATION.NOT_AVAILABLE,
            compensationValue: item.compensation
              ? retrieveCompensationRange(
                  item.compensation.salaryFromId ? [item.compensation.salaryFromId] : undefined,
                  item.compensation.salaryToId ? [item.compensation.salaryToId] : undefined,
                  item.compensation.currency,
                )
              : '-',
            compensation: item.compensation
              ? {
                  staticSalaryFromValue: cachedCompensationData.get(item.compensation.salaryFromId),
                  staticSalaryToValue: cachedCompensationData.get(item.compensation.salaryToId),
                  salaryFromValue: cachedCompensationData.get(item.compensation.salaryFromId),
                  salaryToValue: cachedCompensationData.get(item.compensation.salaryToId),
                  ...item.compensation,
                }
              : {},
          })),
        ),
        positions: positions.map(position => ({
          vacancyId: vacancy.id,
          ...position,
        })),
        competencyRange:
          competency.maxLevel === competency.minLevel
            ? competency.maxLevel
            : `${competency.minLevel} - ${competency.maxLevel}`,
        specializationName:
          specializationsResponse.find(item => item.alias === vacancy.specialization)?.name || vacancy.specialization,
      };
    };

    const mappedVacancies = await Promise.all(vacancies.map(mapVacancy));

    return {
      ...values,
      specialization:
        specializationsResponse.find(item => item.alias === values.specialization)?.name || values.specialization,
      vacancies: mappedVacancies,
      details: {
        competencyLevel:
          competencyLevelMax === competencyLevelMin
            ? competencyLevelMax
            : `${competencyLevelMin} - ${competencyLevelMax}`,
        englishLevel: englishLevelMax === englishLevelMin ? englishLevelMax : `${englishLevelMin} - ${englishLevelMax}`,
        compensations: groupSalaryCompensations,
        hiringModes: hiringModes.map(mode => HIRING_MODE[mode]),
        accommodations: accommodations.includes(
          LOCATION_ACCOMMODATIONS.ANYWHERE as keyof typeof LOCATION_ACCOMMODATIONS,
        )
          ? [LOCATION_ACCOMMODATIONS.ANYWHERE]
          : accommodations,
        ...details,
      },
    };
  };

  const createVacancy = async (values: FormFieldsData) => {
    const tempValues = cloneDeep(values) as VacancyFormType;
    const res = await sendPostRequest('/vacancies/request', mapDataForCreate(tempValues));

    return res.json();
  };

  const updateVacancy = async (id: string, values: FormFieldsData) => {
    const tempValues = cloneDeep(values);

    const res = await sendPutRequest(`/vacancies/${id}`, {
      title: tempValues.title,
      isAsap: tempValues.isAsap,
      hiringMode: tempValues.hiringMode,
      targetHiringDate: tempValues.targetHiringDate,
      responsiblePerson: tempValues.responsiblePerson,
      staffingCoordinator: tempValues.staffingCoordinator,
      workingConditions: await Promise.all(
        tempValues.workingConditions.map(async (item: any) => {
          if (!item.compensation.salaryFromId && !item.compensation.salaryToId && !item.compensation.currency) {
            return {
              ...item,
              isRelocationAvailable: item.isRelocationAvailable === RELOCATION.AVAILABLE,
              compensation: null,
            };
          }

          const salaryFromKpiValue =
            item.compensation.salaryFromValue !== item.compensation.staticSalaryFromValue
              ? await saveSensitiveRecruitingInfo(item.compensation.salaryFromValue)
              : { id: item.compensation.salaryFromId, value: item.compensation.salaryFromValue };
          const salaryToKpiValue =
            item.compensation.salaryToValue !== item.compensation.staticSalaryToValue
              ? await saveSensitiveRecruitingInfo(item.compensation.salaryToValue)
              : { id: item.compensation.salaryToId, value: item.compensation.salaryToValue };

          const salaryFromId = salaryFromKpiValue.id;
          const salaryToId = salaryToKpiValue.id;

          return {
            ...item,
            isRelocationAvailable: item.isRelocationAvailable === RELOCATION.AVAILABLE,
            compensation: {
              id: item.compensation.id,
              currency: item.compensation.currency,
              salaryFromId,
              salaryToId,
            },
          };
        }),
      ),
      projects: tempValues.projects,
      responsibilities: tempValues.responsibilities,
      mandatoryRequirements: tempValues.mandatoryRequirements,
      optionalRequirements: tempValues.optionalRequirements,
      notes: tempValues.notes,
      positions: tempValues.positions.map((item: any) => {
        return {
          id: item.id,
          type: item.type,
          status: item.status,
          forecastDate: item.forecastDate,
          closeDate: item.closeDate,
          startWorkingDate: item.startWorkingDate,
        };
      }),
    });

    return res.json();
  };

  const approveVacancy = async (vacancyId: StringOrNumber) => {
    const res = await sendPostRequest(`/vacancies/${vacancyId}/approve`);

    return res.json();
  };

  const retrieveSensitiveRecruitingInfo = async (ids: string[]) => {
    const kpiValues = await searchSensitiveRecruitingInfo(ids);
    const kpiValuesMap = new Map();

    kpiValues.forEach((kpiValue: any) => {
      kpiValuesMap.set(kpiValue.id, kpiValue.value);
    });

    return kpiValuesMap;
  };

  const searchVacanciesByGroup = async (groupId: StringOrNumber) => {
    const res = await sendPostRequest('/vacancies/search', {
      filter: {
        'vacancyGroup.id': {
          eq: groupId,
        },
      },
      pageSize: 30,
    });

    return res.json();
  };

  const getVacancyGroupById = async (groupId: StringOrNumber) => {
    const res = await sendGetRequest(`${VACANCIES_GROUPS}/${groupId}`);
    const groupDate = await res.json();
    const vacancies = await searchVacanciesByGroup(groupId);
    const setSpecializationsResponse = await getSpecializationOptions();

    const result = await mapVacancyGroup(groupDate, vacancies.result || [], setSpecializationsResponse);

    return result;
  };

  const updateVacancyGroup = async (id: StringOrNumber, values: FormFieldsData) => {
    const res = await sendPutRequest(`${VACANCIES_GROUPS}/${id}`, {
      sourcers: values.sourcers.map((item: any) => item.value),
      recruiters: values.recruiters.map((item: any) => item.value),
      coordinators: values.details.coordinators.map((item: any) => item.value),
      priority: values.priority,
      specificNote: values.specificNote,
      isCandidatesHidden: values.isCandidatesHidden,
      isAutoDecisionTransitionDisabled: values.isAutoDecisionTransitionDisabled,
    });

    return res.json();
  };

  const updateVacancyGroupPositionSlotData = async (
    id: StringOrNumber,
    values: FormFieldsData<VacancyGroupPositionSlotData>,
  ) => {
    const payload = {
      positionSlotOffices: values.positionSlotOffices,
      positionSlotWorkerType: values.positionSlotWorkerType,
    };
    const res = await sendPatchRequest(`${VACANCIES_GROUPS}/${id}/migrate-slot`, payload);
    const response = await res.json();

    return {
      positionSlotOffices: response.positionSlotOffices,
      positionSlotWorkerType: response.positionSlotWorkerType,
    };
  };

  const closeVacancyPosition = async (
    vacancyId: StringOrNumber,
    positionId: StringOrNumber,
    values: FormFieldsData,
  ) => {
    const res = await sendPostRequest(`/vacancies/${vacancyId}/positions/${positionId}/close`, values);

    return res.json();
  };

  const requestVacancyCancellation = async (vacancyId: StringOrNumber, cancellationReason: any) => {
    const res = await sendPostRequest(`/vacancies/${vacancyId}/request-cancellation`, {
      cancelReason: cancellationReason,
    });

    return res.json();
  };

  const requestVacancyChange = async (vacancyId: StringOrNumber, changeNote: string) => {
    const res = await sendPostRequest(`/vacancies/${vacancyId}/changes/request`, {
      changeNote,
    });

    return res.json();
  };

  const searchVacancyChangeRequests = async (vacancyId: StringOrNumber, status: 'ACTIVE' | 'RESOLVED') => {
    const res = await sendPostRequest(`/vacancies/${vacancyId}/changes/search`, {
      searchRequest: {
        filter: {
          'vacancy.id': {
            eq: vacancyId,
          },
          status: {
            eq: status,
          },
        },
        sort: { createdAt: 'DESC' },
      },
    });

    return res.json();
  };

  const resolveVacancyChangeRequest = async (vacancyId: StringOrNumber, changeRequestId: StringOrNumber) => {
    const res = await sendPostRequest(`/vacancies/${vacancyId}/changes/${changeRequestId}/resolve`);

    return res.json();
  };

  const approveVacancyCancellation = async (vacancyId: StringOrNumber) => {
    const res = await sendPostRequest(`/vacancies/${vacancyId}/cancellation-request/approve`);

    return res.json();
  };

  const declineVacancyCancellation = async (vacancyId: StringOrNumber) => {
    const res = await sendPostRequest(`/vacancies/${vacancyId}/cancellation-request/decline`);

    return res.json();
  };

  const closeVacancy = async (vacancyId: StringOrNumber) => {
    const res = await sendPostRequest(`/vacancies/${vacancyId}/close`);

    return res.json();
  };

  const closeVacancyGroup = async (vacancyGroupId: StringOrNumber) => {
    const res = await sendPostRequest(`/vacancies/groups/${vacancyGroupId}/close`);

    return res.json();
  };

  const cancelVacancyBeforeReview = async (vacancyId: StringOrNumber, cancellationReason: any) => {
    const res = await sendPostRequest(`/vacancies/${vacancyId}/cancel`, {
      cancelReason: cancellationReason,
    });

    return res.json();
  };

  const getCreateValidationSchema = () => {
    if (!positionSlotsEnabled) {
      return Promise.resolve(createVacancySchema);
    }

    const requiredForStandardFlow = {
      value: true,
      when: {
        $vacancyCreateFlow: {
          eq: VACANCY_CREATE_FLOW.STANDARD,
        },
      },
    };
    const requiredForPositionSlotFlow = {
      value: true,
      when: {
        $vacancyCreateFlow: {
          eq: VACANCY_CREATE_FLOW.POSITION_SLOTS,
        },
      },
    };
    const { workingConditions } = createVacancySchema;

    return Promise.resolve({
      ...createVacancySchema,
      vacancyCreateFlow: {
        type: 'enum',
        required: true,
      },
      peopleToHireMin: {
        ...createVacancySchema.peopleToHireMin,
        required: requiredForStandardFlow,
      },
      peopleToHireMax: {
        ...createVacancySchema.peopleToHireMax,
        required: requiredForStandardFlow,
      },
      workingConditions: {
        ...workingConditions,
        forEach: {
          ...workingConditions.forEach,
          properties: {
            hiringLocation: {
              ...workingConditions.forEach.properties.hiringLocation,
              required: requiredForStandardFlow,
            },
            employmentTypes: workingConditions.forEach.properties.employmentTypes,
            contractTypes: workingConditions.forEach.properties.contractTypes,
            isRelocationAvailable: workingConditions.forEach.properties.isRelocationAvailable,
          },
        },
      },
      workerType: {
        type: 'string',
        required: requiredForPositionSlotFlow,
      },
      positionSlotIds: {
        type: 'array',
        required: requiredForPositionSlotFlow,
        minSize: {
          value: 1,
          when: {
            $vacancyCreateFlow: {
              eq: VACANCY_CREATE_FLOW.POSITION_SLOTS,
            },
          },
        },
      },
    });
  };
  const getUpdateValidationSchema = () => Promise.resolve(updateVacancySchema);
  const getUpdateRequiresReviewValidationSchema = () => Promise.resolve(updateRequiresReviewVacancySchema);
  const getClosePositionValidationSchema = () => Promise.resolve(closePositionValidationSchema);

  return {
    mapDataForForm,
    closeVacancyPosition,
    updateVacancyGroup,
    getVacancyGroupById,
    searchVacancy,
    moveToGroup,
    updateVacancy,
    createVacancy,
    approveVacancy,
    mapLegacyVacancy,
    saveSensitiveRecruitingInfo,
    retrieveSensitiveRecruitingInfo,
    migrateVacancy,
    getCreateValidationSchema,
    getUpdateValidationSchema,
    getUpdateRequiresReviewValidationSchema,
    getClosePositionValidationSchema,
    searchVacanciesByGroup,
    baseSearchAll,
    requestVacancyChange,
    searchVacancyChangeRequests,
    resolveVacancyChangeRequest,
    requestVacancyCancellation,
    approveVacancyCancellation,
    declineVacancyCancellation,
    closeVacancy,
    closeVacancyGroup,
    updateVacancyGroupPositionSlotData,
    changePriority,
    visitGroup,
    cancelVacancyBeforeReview,
    ...baseCrud,
  };
};

export default useVacancyGroupService;
